import { useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, BriefcaseBusiness, Phone, SearchX, UserRound } from 'lucide-react'

import { assignmentsApi } from '@/services/assignments'
import { catalogRolesApi, type CatalogRole } from '@/services/catalog-roles'
import { workersApi, type WorkerSkill } from '@/services/workers'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getAssignmentStatusBadgeClass, getAssignmentStatusLabel, resolveAssignmentStatus, type AssignmentStatus } from '@/lib/assignment-status'
import { unmaskMaskedId } from '@/lib/backend-ids'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import type { Assignment, Worker } from '@/types'

type WorkerAssignmentSummary = {
  pending: number
  accepted: number
  activeTotal: number
  statuses: AssignmentStatus[]
}

const ACTIVE_STATUS_ORDER: AssignmentStatus[] = ['pending', 'accepted']

const asText = (value: unknown): string => String(value ?? '').trim()

const roleMatchesSkillId = (role: CatalogRole, skillId: string) => {
  const rawRoleId = unmaskMaskedId(role._id)
  return [role.id, role._id, rawRoleId, role.unique_id]
    .map(asText)
    .filter(Boolean)
    .includes(skillId)
}

const workerMatchesSkill = (worker: Worker, skill: WorkerSkill) => {
  const skillWorkerId = asText(skill.worker)
  return [worker.id, worker.worker_id, worker.user_id]
    .map(asText)
    .filter(Boolean)
    .includes(skillWorkerId)
}

const getWorkerName = (worker?: Worker | null, skill?: WorkerSkill) => {
  return asText(worker?.display_name)
    || asText(worker?.full_name)
    || [asText(worker?.first_name), asText(worker?.last_name)].filter(Boolean).join(' ')
    || asText(skill?.worker_display_name)
    || `Worker ${asText(skill?.worker)}`
}

const getWorkerPhone = (worker?: Worker | null) => asText(worker?.phone_number) || asText(worker?.phone) || '-'

const getWorkerRegNo = (worker?: Worker | null) => asText(worker?.user_registration_number) || '-'

const getWorkerCategory = (skill?: WorkerSkill | null) => {
  return asText(skill?.job_category_name)
    || asText(skill?.job_category_title)
    || asText(skill?.job_category)
    || '-'
}

const summarizeAssignments = (assignments: Assignment[]): WorkerAssignmentSummary => {
  const activeAssignments = assignments.filter((assignment) => {
    const status = resolveAssignmentStatus(assignment.status)
    return status === 'pending' || status === 'accepted'
  })
  const statuses = ACTIVE_STATUS_ORDER.filter((status) => activeAssignments.some((assignment) => resolveAssignmentStatus(assignment.status) === status))

  return {
    pending: activeAssignments.filter((assignment) => resolveAssignmentStatus(assignment.status) === 'pending').length,
    accepted: activeAssignments.filter((assignment) => resolveAssignmentStatus(assignment.status) === 'accepted').length,
    activeTotal: activeAssignments.length,
    statuses,
  }
}

const buildWorkerAssignmentsPath = (workerId: string, serviceInstanceId?: string | null, status?: AssignmentStatus) => {
  const params = new URLSearchParams()
  params.set('worker_id', workerId)
  if (status) {
    params.set('status', status)
  }
  if (serviceInstanceId) {
    params.set('service_instance_id', serviceInstanceId)
  }
  return `/assignments?${params.toString()}`
}

export default function JobSkillWorkersPage() {
  const { skillId = '' } = useParams<{ skillId: string }>()
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)

  const catalogRolesQuery = useQuery({
    queryKey: ['catalog-roles', 'skill-workers', serviceInstanceId],
    queryFn: () => catalogRolesApi.list({ serviceInstanceId, includeHidden: true }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 30_000,
  })

  const directWorkerSkillsQuery = useQuery({
    queryKey: ['worker-skills', 'by-role', serviceInstanceId, skillId],
    queryFn: () => workersApi.getWorkerSkills({
      skill_role: skillId,
      service_instance_id: serviceInstanceId || undefined,
      is_active: true,
      page_size: 500,
    }),
    enabled: Boolean(skillId && serviceInstanceId),
    staleTime: 30_000,
  })

  const workerSkillDetailQuery = useQuery({
    queryKey: ['worker-skill', 'skill-workers-fallback', skillId],
    queryFn: () => workersApi.getWorkerSkill(skillId),
    enabled: Boolean(skillId && directWorkerSkillsQuery.isSuccess && (directWorkerSkillsQuery.data?.length ?? 0) === 0),
    retry: false,
    staleTime: 30_000,
  })

  const resolvedRoleId = asText(workerSkillDetailQuery.data?.skill_role) || skillId

  const fallbackWorkerSkillsQuery = useQuery({
    queryKey: ['worker-skills', 'by-fallback-role', serviceInstanceId, resolvedRoleId],
    queryFn: () => workersApi.getWorkerSkills({
      skill_role: resolvedRoleId,
      service_instance_id: serviceInstanceId || undefined,
      is_active: true,
      page_size: 500,
    }),
    enabled: Boolean(serviceInstanceId && resolvedRoleId && resolvedRoleId !== skillId),
    staleTime: 30_000,
  })

  const workersQuery = useQuery({
    queryKey: ['workers', 'skill-workers', serviceInstanceId],
    queryFn: () => workersApi.getWorkers({ page_size: 500, service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 30_000,
  })

  const workerSkills = useMemo(() => {
    const primary = directWorkerSkillsQuery.data ?? []
    if (primary.length > 0) {
      return primary
    }

    const fallback = fallbackWorkerSkillsQuery.data ?? []
    if (fallback.length > 0) {
      return fallback
    }

    return workerSkillDetailQuery.data ? [workerSkillDetailQuery.data] : []
  }, [directWorkerSkillsQuery.data, fallbackWorkerSkillsQuery.data, workerSkillDetailQuery.data])

  const workers = workersQuery.data?.results ?? []
  const role = useMemo(() => (catalogRolesQuery.data ?? []).find((candidate) => roleMatchesSkillId(candidate, resolvedRoleId)), [catalogRolesQuery.data, resolvedRoleId])
  const skillTitle = asText(role?.name) || asText(role?.title) || asText(workerSkills[0]?.skill_role_name) || `Skill ${skillId}`

  const workerRows = useMemo(() => {
    const seen = new Set<string>()
    return workerSkills
      .filter((skill) => skill.is_active !== false)
      .map((skill) => {
        const worker = workers.find((candidate) => workerMatchesSkill(candidate, skill)) ?? null
        const key = asText(worker?.worker_id) || asText(skill.worker) || asText(worker?.id)
        return { key, skill, worker }
      })
      .filter((row) => {
        if (!row.key || seen.has(row.key)) {
          return false
        }
        seen.add(row.key)
        return true
      })
      .sort((left, right) => getWorkerName(left.worker, left.skill).localeCompare(getWorkerName(right.worker, right.skill)))
  }, [workerSkills, workers])

  const workerAssignmentSummariesQuery = useQuery({
    queryKey: ['assignments', 'skill-workers', workerRows.map((row) => row.key).sort().join('|')],
    queryFn: async () => {
      const entries = await Promise.all(workerRows.map(async (row) => {
        const response = await assignmentsApi.getAssignments({ worker_id: row.key, page: 1, page_size: 200 })
        return [row.key, summarizeAssignments(response.results)] as const
      }))
      return new Map<string, WorkerAssignmentSummary>(entries)
    },
    enabled: workerRows.length > 0,
    staleTime: 30_000,
  })

  const isLoading = directWorkerSkillsQuery.isLoading || workersQuery.isLoading || catalogRolesQuery.isLoading
  const activeJobTotal = workerRows.reduce((sum, row) => sum + (workerAssignmentSummariesQuery.data?.get(row.key)?.activeTotal ?? 0), 0)

  return (
    <div className="space-y-4 p-4 md:p-6">
      <section className="flex flex-col gap-3 rounded-2xl border border-border/60 bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex items-start gap-3">
          <Button variant="ghost" size="icon" className="mt-0.5 h-10 w-10 shrink-0" asChild>
            <Link to={withServiceInstanceId('/org/catalog-roles', serviceInstanceId)} title="Back to role skills">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">{skillTitle}</h1>
            <p className="mt-1 text-sm text-muted-foreground">Workers assigned to this role skill.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
            <UserRound className="mr-1 h-3.5 w-3.5" />
            Workers {workerRows.length}
          </Badge>
          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
            <BriefcaseBusiness className="mr-1 h-3.5 w-3.5" />
            Active assignments {activeJobTotal}
          </Badge>
        </div>
      </section>

      <Card className="rounded-2xl border-border/60 bg-card shadow-sm">
        <CardHeader className="border-b border-border/60 pb-3">
          <CardTitle className="text-base font-semibold">Assigned Workers</CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-4">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : workerRows.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-border/70 bg-background">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/35 hover:bg-muted/35">
                    <TableHead className="h-11 pl-4 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Worker</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Reg No</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Phone</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Category</TableHead>
                    <TableHead className="text-right text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Pending</TableHead>
                    <TableHead className="text-right text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Accepted</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Statuses</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workerRows.map((row) => {
                    const worker = row.worker
                    const summary = workerAssignmentSummariesQuery.data?.get(row.key) ?? summarizeAssignments([])
                    const workerProfileId = asText(worker?.id) || asText(row.skill.worker)
                    return (
                      <TableRow key={row.key} className="border-border/60">
                        <TableCell className="pl-4">
                          <Link
                            to={withServiceInstanceId(`/workers/${workerProfileId}`, serviceInstanceId)}
                            className="font-medium text-primary hover:underline"
                          >
                            {getWorkerName(worker, row.skill)}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{getWorkerRegNo(worker)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <Phone className="h-3.5 w-3.5" />
                            {getWorkerPhone(worker)}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{getWorkerCategory(row.skill)}</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {summary.pending > 0 ? (
                            <Link to={buildWorkerAssignmentsPath(row.key, serviceInstanceId, 'pending')} className="text-primary hover:underline">
                              {summary.pending}
                            </Link>
                          ) : '0'}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {summary.accepted > 0 ? (
                            <Link to={buildWorkerAssignmentsPath(row.key, serviceInstanceId, 'accepted')} className="text-primary hover:underline">
                              {summary.accepted}
                            </Link>
                          ) : '0'}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1.5">
                            {summary.statuses.length > 0 ? summary.statuses.map((status) => (
                              <Badge key={status} variant="outline" className={getAssignmentStatusBadgeClass(status)}>
                                {getAssignmentStatusLabel(status)}
                              </Badge>
                            )) : (
                              <span className="text-sm text-muted-foreground">No active jobs</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex min-h-40 flex-col items-center justify-center rounded-xl border border-dashed border-border/70 p-6 text-center text-muted-foreground">
              <SearchX className="mb-2 h-8 w-8" />
              <p className="text-sm">No workers are assigned to this role skill.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}